import { CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from './money-formatting.js';

class ProductAddOnsComponent extends HTMLElement {
  #selectedAddOns = new Map(); // {addonId: {price, title, variantId}}
  #form = null;
  #checkboxes = null;
  #totalElement = null;
  #isSubmitting = false;
  #zoomTriggers = [];
  #buttons = [];
  #moneyFormat = '{{amount}}';
  #currency = 'USD';

  connectedCallback() {
    this.#cacheElements();
    this.#attachEventListeners();
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#handleFormSubmit, true);
    this.#buttons.forEach(btn => {
      btn.removeEventListener('click', this.#handleButtonClick, true);
    });
    this.#checkboxes?.forEach(checkbox => {
      checkbox.removeEventListener('change', this.#handleCheckboxChange);
    });
    this.#zoomTriggers?.forEach(trigger => {
      trigger.removeEventListener('click', this.#handleZoomTriggerClick);
    });
  }

  #cacheElements() {
    const formId = this.dataset.productFormId;
    this.#form = document.getElementById(formId);
    this.#checkboxes = this.querySelectorAll('input[type="checkbox"]');
    this.#totalElement = this.querySelector('[data-addon-total]');
    this.#zoomTriggers = Array.from(this.querySelectorAll('.product-add-ons__image-zoom-trigger'));
    this.#moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.#currency = this.dataset.currency || 'USD';

    // In collapsible mode the total lives in the summary (outside this component).
    if (!this.#totalElement) {
      const wrapper = this.closest('.product-add-ons__wrapper');
      if (wrapper instanceof HTMLElement) {
        this.#totalElement = wrapper.querySelector('[data-addon-total]');
      }
    }
  }

  #attachEventListeners() {
    if (!this.#form) return;

    // Listen to all checkboxes for change
    this.#checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', this.#handleCheckboxChange);
    });

    // Attach zoom trigger listeners
    this.#zoomTriggers.forEach((trigger, index) => {
      trigger.addEventListener('click', (event) => this.#handleZoomTriggerClick(event, index));
    });

    // Intercept form submission in capture phase
    this.#form.addEventListener('submit', this.#handleFormSubmit, true);

    // Also intercept add-to-cart button clicks to prevent ProductFormComponent from firing
    this.#buttons = Array.from(this.#form.querySelectorAll('button[type="submit"][name="add"]'));
    this.#buttons.forEach(btn => {
      btn.addEventListener('click', this.#handleButtonClick, true);
    });
  }

  #handleButtonClick = (event) => {
    // If there are add-ons, prevent default and handle submission ourselves
    if (this.#selectedAddOns.size > 0) {
      event.preventDefault();
      event.stopPropagation();
      this.#submitWithAddOns();
    }
  };

  #handleCheckboxChange = (event) => {
    const checkbox = event.target;
    const addonId = checkbox.dataset.addonId;
    const isChecked = checkbox.checked;

    if (isChecked) {
      this.#selectedAddOns.set(addonId, {
        variantId: checkbox.dataset.addonVariantId,
        price: parseInt(checkbox.dataset.addonPrice),
        title: checkbox.dataset.addonTitle,
        productId: checkbox.dataset.addonProductId,
      });
    } else {
      this.#selectedAddOns.delete(addonId);
    }

    this.#updateTotal();
  };

  #updateTotal() {
    if (!this.#totalElement) return;

    const total = Array.from(this.#selectedAddOns.values()).reduce(
      (sum, addon) => sum + addon.price,
      0
    );

    if (this.#selectedAddOns.size > 0) {
      this.#totalElement.parentElement.hidden = false;
      this.#totalElement.textContent = this.#formatPrice(total);
    } else {
      this.#totalElement.parentElement.hidden = true;
    }
  }

  #formatPrice(price) {
    return formatMoney(price, this.#moneyFormat, this.#currency);
  }

  async #submitWithAddOns() {
    if (this.#isSubmitting) return;
    this.#isSubmitting = true;

    try {
      const formData = new FormData(this.#form);
      const mainProductData = this.#getMainProductData(formData);

      // Build items array: main product + add-ons
      const itemsToAdd = [mainProductData];

      // Add each selected add-on as separate line item
      if (this.#selectedAddOns.size > 0) {
        for (const addon of this.#selectedAddOns.values()) {
          itemsToAdd.push({
            id: addon.variantId,
            quantity: 1,
            properties: {
              _add_on: 'true',
              _add_on_title: addon.title,
            },
          });
        }
      }

      // Add all items to cart
      const itemCount = itemsToAdd.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      const deferredEventPromise = CartLinesUpdateEvent.createPromise();

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: itemsToAdd.map((item) => ({
            merchandiseId: String(item.id),
            quantity: Number(item.quantity || 1),
          })),
          promise: deferredEventPromise.promise,
        })
      );

      const { cart, response } = await this.#addToCart(itemsToAdd);

      const ajaxCart = await fetch('/cart.js').then((r) => r.json());
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'product-add-ons-component',
          sourceId: this.id,
          itemCount,
          productId: this.dataset.productId,
          sections: response.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error('Error adding items to cart:', error);
    } finally {
      this.#isSubmitting = false;
    }
  }

  #handleZoomTriggerClick = (event, index) => {
    event.preventDefault();
    event.stopPropagation();

    // Find zoom dialog in the parent wrapper
    const wrapper = this.closest('.product-add-ons__wrapper');
    const zoomDialog = wrapper?.querySelector('zoom-dialog');
    if (!zoomDialog) return;

    // Call the zoom-dialog component's open method
    zoomDialog.open(index, event);

    // Focus the close button after dialog renders
    requestAnimationFrame(() => {
      const closeButton = zoomDialog.querySelector('.dialog-zoomed-gallery__close-button');
      if (closeButton instanceof HTMLElement) {
        closeButton.focus();
      }
    });
  };

  #isAddToCartSubmitter(submitter) {
    return (
      submitter instanceof HTMLElement &&
      submitter.matches('button[type="submit"][name="add"], input[type="submit"][name="add"]')
    );
  }

  #handleFormSubmit = async (event) => {
    // Only intercept if there are selected add-ons
    const submitter = event.submitter instanceof HTMLElement ? event.submitter : document.activeElement;
    if (this.#selectedAddOns.size === 0 || !this.#isAddToCartSubmitter(submitter)) {
      return; // Let ProductFormComponent handle the submission
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    await this.#submitWithAddOns();
  };

  #getMainProductData(formData) {
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity')) || 1;

    const properties = {};
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[')) {
        const propName = key.match(/properties\[(.*?)\]/)[1];
        properties[propName] = value;
      }
    }

    return {
      id: variantId,
      quantity: quantity,
      ...(Object.keys(properties).length > 0 && { properties }),
    };
  }

  async #addToCart(items) {
    const sections = this.#getSectionIds();
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items,
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }),
    });

    const responseData = await response.json();
    if (!response.ok) {
      throw new Error(responseData.message || 'Failed to add items to cart');
    }

    return {
      response: responseData,
    };
  }

  async #fetchCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }

      return response.json();
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      return null;
    }
  }

  #getSectionIds() {
    const sectionIds = new Set();

    if (this.dataset.sectionId) {
      sectionIds.add(this.dataset.sectionId);
    }

    document.querySelectorAll('cart-items-component').forEach(component => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    });

    return Array.from(sectionIds);
  }
}

customElements.define('product-add-ons-component', ProductAddOnsComponent);
